import React, { useEffect, useId, useMemo, useRef } from "react";

/**
 * Минимальная модалка под стиль проекта.
 *
 * props:
 * - open: boolean
 * - title: string
 * - onClose: () => void
 * - actions: ReactNode (кнопки в футере)
 * - size: "sm" | "md" | "lg" (опционально)
 * - className / cardClassName (опционально)
 */
export default function Modal({
    open,
    title,
    onClose,
    children,
    actions,
    size = "md",
    className = "",
    cardClassName = "",
}) {
    const titleId = useId();
    const cardRef = useRef(null);
    const closeBtnRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document?.body?.style?.overflow;
        const prevActive = typeof document !== "undefined" ? document.activeElement : null;
        if (document?.body?.style) document.body.style.overflow = "hidden";

        const focusTimer = window.setTimeout(() => {
            closeBtnRef.current?.focus?.();
        }, 0);

        return () => {
            window.clearTimeout(focusTimer);
            if (document?.body?.style) document.body.style.overflow = prevOverflow || "";
            prevActive?.focus?.();
        };
    }, [open]);

    const wrapperClassName = useMemo(
        () => ["b-modal", open ? "is-open" : "", className].filter(Boolean).join(" "),
        [className, open]
    );
    const cardSizeClass = useMemo(() => {
        const normalized = String(size || "md").trim().toLowerCase();
        if (normalized === "sm" || normalized === "lg") return `is-${normalized}`;
        return "is-md";
    }, [size]);
    const resolvedCardClassName = useMemo(
        () => ["b-modal__card", cardSizeClass, cardClassName].filter(Boolean).join(" "),
        [cardClassName, cardSizeClass]
    );

    const onCardKeyDown = (event) => {
        if (event.key !== "Tab") return;
        const root = cardRef.current;
        if (!root) return;
        const focusable = Array.from(
            root.querySelectorAll(
                'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
            )
        ).filter((node) => node && typeof node.focus === "function" && node.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey) {
            if (document.activeElement === first || document.activeElement === root) {
                event.preventDefault();
                last.focus();
            }
            return;
        }
        if (document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    if (!open) return null;

    return (
        <div className={wrapperClassName} role="dialog" aria-modal="true" aria-labelledby={titleId}>
            <button className="b-modal__backdrop" type="button" onClick={onClose} aria-label="Закрыть" />

            <div className={resolvedCardClassName} ref={cardRef} onKeyDown={onCardKeyDown}>
                <div className="b-modal__head">
                    <div className="b-modal__title" id={titleId}>
                        {title}
                    </div>
                    <button
                        ref={closeBtnRef}
                        className="b-modal__close"
                        type="button"
                        onClick={onClose}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>
                </div>

                <div className="b-modal__body">{children}</div>

                {actions ? <div className="b-modal__foot">{actions}</div> : null}
            </div>
        </div>
    );
}
